import {
    Component, ViewEncapsulation, ContentChildren, QueryList,
    Input, OnInit, Output, ViewChild, ContentChild, OnDestroy
} from '@angular/core';
import {environment} from '@env';
import { CfaMenuItem } from '../tabs/cfa-menu-item';
import { CfaTab } from '../tabs/cfa-tab';
import { IEnum } from '../models/IEnum';
import { Subject, Subscription } from 'rxjs';
import { CfaToolbar } from '../toolbar/toolbar';
import { CfaBottomTabContainer } from '../tabs/bottom-tab-container/bottom-tab-container';
import { CfaNavContent } from './cfa-nav-content';
import { ISearchObject } from '../models/ISearchObject';
import { CfaCartButton } from '../buttons/cart-button/cart-button';
import { Router } from '@angular/router';
import {FlagKeys, LaunchDarklyService} from '@emm/core/services/launch-darkly.service';

@Component({
       selector: 'cfa-navigation-wrapper',
    templateUrl: './navigation-wrapper.html',
    styleUrls: ['../../styles/styles.scss', './navigation-wrapper.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class CfaNavigationWrapper implements OnInit, OnDestroy {

    private MobileNavTypeEnum: IEnum[] = [
        { key: 'Hamburger', value: 0 },
        { key: 'BottomBar', value: 1 }
    ];

    /** Sets the Base Route of the app for use in routing (Defaults to '/') */
    @Input() baseRoute = '/menu';

    /** Use a custom header element in place of tabs (Defaults to false) */
    @Input() customHeader = false;

    /** Sets the Mobile Nav Type. Options are 'Hambuger' or 'BottomBar' (Defaults to 'Hambuger') */
    @Input() mobileNavType = 'Hamburger';

    /** Shows drop shadows under the Toolbar and Bottom Nav bar (Defaults to false) */
    @Input() showDropShadow = false;

    /** Overrides default theme to accept Angular Material Theme */
    @Input() useMaterialTheme = false;

    /** Removes Bootstraps container padding and max-width contraints (Defaults to false) */
    @Input() ignoreContainerPadding = false;

    /** Sets the App Name for display in the App Brand Button */
    @Input() appName: string;

    /** Sets the Short App name for use on mobile when tab pages aren't set */
    @Input() shortAppName: string;

    /** Sets the user's name to display on the toolbar and in menus */
    @Input() userName: string;

    /** Whether or not to display a cart in the toolbar (Defaults to false) */
    @Input() cartEnabled: false;

    /** Sets the icon for the cart (Defaults to 'Bag') */
    @Input() cartIcon = 'Bag';

    /** Whether or not to show a total under the cart icon (Defaults to false) */
    @Input() showCartTotal: false;

    /** Whether or not to show a bage count over the cart icon (Defaults to false) */
    @Input() showCartBadge: false;

    /** Whether or not to show a loading indicator over the cart (Defaults to false) */
    @Input() cartLoading: false;

    /** Sets the active search result (Typically a Store) for display in the toolbar */
    @Input() activeSearchObject: ISearchObject;

    /** Sets the list of search objects (Typically stores) for use in the navigation search */
    @Input() searchObjects: ISearchObject[] = [];

    /** Overrides default Navigation Search Placeholder Name (Defaults to 'Stores') */
    @Input() navSearchObjectName = 'Stores';

    /** Overrides default Nav Search Filter Function */
    @Input() navSearchFilterFunction: (search: string, objects: ISearchObject[]) => ISearchObject[];

    /** Event fires whenever a sign out button or link is clicked */
    @Output() signout = new Subject<void>();

    /** Event fires whenever the app brand button or mobile title are clicked */
    @Output() goHome = new Subject<void>();

    /** Event first whenever the cart is clicked */
    @Output() openCart = new Subject<void>();

    /** Event fires whenever a store is selected. Payload is the store number string */
    @Output() searchResultSelected: Subject<string> = new Subject<string>();

    @ViewChild(CfaToolbar) cfaToolbar: CfaToolbar;
    @ViewChild(CfaBottomTabContainer) cfaBottomBar: CfaBottomTabContainer;
    @ViewChild(CfaCartButton) cartButton: CfaCartButton;

    protected readonly environment = environment;
    isProd = environment.name === 'prod';
    showMenuPane = false;
    distance = 0;
    calculatedDistance = -100;
    @ContentChild(CfaNavContent) navContent: CfaNavContent;

    cfaTabs: CfaTab[] = [];
    private tSubs: Subscription[] = [];
    @ContentChildren(CfaTab) set setTabs(cfaTabs: QueryList<CfaTab>) {
        this.tSubs.forEach(s => s.unsubscribe());
        this.tSubs = [];

        if (cfaTabs) {
            this.cfaTabs = cfaTabs.toArray();
            this.cfaTabs.forEach(tab => {
                const tSub = tab.selected.subscribe(() => {
                    this.selectTab(tab);
                });
                this.tSubs.push(tSub);

                const cTSub = tab.childTabSelected.subscribe(cTab => {
                    this.selectTab(cTab);
                });
                this.tSubs.push(cTSub);
            });
        } else {
            this.cfaTabs = [];
        }
    }

    cfaMenuItems: CfaMenuItem[] = [];
    private mSubs: Subscription[] = [];
    @ContentChildren(CfaMenuItem) set setMenuItems(cfaMenuItems: QueryList<CfaMenuItem>) {
        this.mSubs.forEach(m => m.unsubscribe());
        this.mSubs = [];

        if (cfaMenuItems) {
            this.cfaMenuItems = cfaMenuItems.toArray();
            this.cfaMenuItems.forEach(menuItem => {
                const mSub = menuItem.selected.subscribe(() => {
                    this.selectMenuItem(menuItem);
                });
                this.mSubs.push(mSub);
            });
        } else {
            this.cfaMenuItems = [];
        }
    }

    constructor(private router: Router, private launchDarklyService: LaunchDarklyService) {}

    ngOnInit() {
        this.launchDarklyService.getBooleanFlag(FlagKeys.USE_COMMON_ITEMS).subscribe(showCommonItems => {
            if (!showCommonItems) {
                this.cfaTabs = this.cfaTabs.filter((tab) => tab.routerLink !== '/common-items');
            }
        });
        if (this.navContent) {
            if (this.customHeader) {
                this.navContent.setCustomHeaderPadding(true);
            }
        }
    }

    ngOnDestroy() {
        this.tSubs?.forEach(t => t.unsubscribe());
        this.mSubs?.forEach(m => m.unsubscribe());
    }

    calculateDistance() {
        this.distance = (this.distance / window.innerWidth) * 100;
        if (this.distance >= 100) {
            this.calculatedDistance = 0;
        } else if (this.distance <= 0) {
            this.calculatedDistance = -110;
        } else {
            this.calculatedDistance = -100 + this.distance;
        }
    }

    /**
     * Sets the Cart Badge number and animates the change
     * @param count The number to display in the Cart Badge
     */
    public setCartBadgeCount(count: number) {
        if (this.cartButton) {
            this.cartButton.setCartBadgeCount(count);
        }
    }

    /**
     * Sets the Cart Total amount
     * @param amount The amount to display under the Cart Icon
     */
    public setCartTotalAmount(amount: number) {
        if (this.cartButton) {
            this.cartButton.setCartTotal(amount);
        }
    }

    getMobileNavType() {
        const mobileNavType = this.MobileNavTypeEnum.find(e => e.key === this.mobileNavType);
        if (mobileNavType === null) {
            throw console.error('');
        }
        return mobileNavType.value;
    }

    signoutClicked() {
        this.signout.next();
    }

    selectTab(tab: CfaTab) {
        if (tab.routerLink) {
            this.router.navigate([tab.routerLink]);
        }
    }

    getActiveTabName(tab: CfaTab) {
        if (!tab.active) {
            return tab.label;
        } else {
            const ctab = this.getActiveChildTab(tab);
            return ctab ? ctab.label : tab.label;
        }
    }

    getActiveChildTab(tab: CfaTab) {
        return tab.childTabs.find(t => t.active);
    }

    getMobileTitle(): string {
        let mobileTitle: string;
        this.cfaTabs.forEach(t => {
            if (t.active) {
                mobileTitle = t.label;
            }
            t.childTabs.forEach(cT => {
                if (cT.active) {
                    mobileTitle = cT.label;
                }
            });
        });
        if (mobileTitle) {
            return mobileTitle;
        } else if (this.shortAppName) {
            return this.shortAppName;
        } else {
            return this.appName;
        }
    }

    selectMenuItem(menuItem: CfaMenuItem) {
        if (menuItem.routerLink !== undefined && menuItem.routerLink !== null) {
            this.router.navigate([menuItem.routerLink]);
        }
    }

    homeClicked() {
        this.router.navigate([this.baseRoute]);
    }

    cartClicked() {
        this.openCart.next();
    }

    selectedStoreChanged(storeNumber: string) {
        this.searchResultSelected.next(storeNumber);
    }

}
